import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { TasasRepository } from '@app/domain/ports/tpv/tasas.repository';
import {
  TasaPatchBody,
  TasaPatchResponse,
  TasaRead,
} from '@app/data/dtos/tpv/tasas.dto';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';

/**
 * HTTP de `/tasa/` en los 3 niveles (contrato §1). Auth vía interceptores.
 *
 * GET: tolera 404 (nodo sin tasas capturadas todavía) devolviendo `null` para
 * que la pantalla arranque con los 4 ejes en 0 % (RN-05). Cualquier otro error
 * (401/403/5xx) SÍ propaga: la UI necesita distinguir "sin capturar" de "no
 * puedes leer".
 *
 * PATCH: propaga el `TasaPatchResponse` (envuelto en `extra_data`) sin tocar
 * — la pantalla usa `push_micro.skipped`, `sync_marcados` y `propagados` según
 * el nivel. Los errores del contrato (400 "Tasa invalida" / 409 "Jerarquia sin
 * tasa configurada" / 403 en comercio / 409 "Company sin provisionar") viajan
 * como HttpErrorResponse — el contenedor los mapea a los mensajes traducidos
 * genéricos (RN-11: JAMÁS mostrar el `detail` con el valor del piso).
 */
@Injectable()
export class TasasRemoteDataSource extends TasasRepository {
  private readonly http = inject(HttpClient);

  // ── Empresa (Tasa Banco) ───────────────────────────────────────────────────
  override getCompanyTasa(idCompany: string): Observable<TasaRead | null> {
    return this.http
      .get<TpvEnvelope<TasaRead> | TasaRead>(tpvUrl(`companies/${idCompany}/tasa/`))
      .pipe(
        map((resp) => this.toReadOrNull(resp)),
        catchError((err: unknown) => this.readOrRethrow(err)),
      );
  }

  override updateCompanyTasa(
    idCompany: string,
    body: TasaPatchBody,
  ): Observable<TasaPatchResponse> {
    return this.http
      .patch<TpvEnvelope<TasaPatchResponse> | TasaPatchResponse>(
        tpvUrl(`companies/${idCompany}/tasa/`),
        body,
      )
      .pipe(map((resp) => unwrapTpv(resp) as TasaPatchResponse));
  }

  // ── Unidad (Tasa Reseller) ─────────────────────────────────────────────────
  override getUnitTasa(idCompany: string, idUnit: string): Observable<TasaRead | null> {
    return this.http
      .get<TpvEnvelope<TasaRead> | TasaRead>(
        tpvUrl(`companies/${idCompany}/units/${idUnit}/tasa/`),
      )
      .pipe(
        map((resp) => this.toReadOrNull(resp)),
        catchError((err: unknown) => this.readOrRethrow(err)),
      );
  }

  override updateUnitTasa(
    idCompany: string,
    idUnit: string,
    body: TasaPatchBody,
  ): Observable<TasaPatchResponse> {
    return this.http
      .patch<TpvEnvelope<TasaPatchResponse> | TasaPatchResponse>(
        tpvUrl(`companies/${idCompany}/units/${idUnit}/tasa/`),
        body,
      )
      .pipe(map((resp) => unwrapTpv(resp) as TasaPatchResponse));
  }

  // ── Comercio (Tasa Comercio) ───────────────────────────────────────────────
  override getMerchantTasa(idMerchant: string): Observable<TasaRead | null> {
    return this.http
      .get<TpvEnvelope<TasaRead> | TasaRead>(tpvUrl(`merchants/${idMerchant}/tasa/`))
      .pipe(
        map((resp) => this.toReadOrNull(resp)),
        catchError((err: unknown) => this.readOrRethrow(err)),
      );
  }

  override updateMerchantTasa(
    idMerchant: string,
    body: TasaPatchBody,
  ): Observable<TasaPatchResponse> {
    return this.http
      .patch<TpvEnvelope<TasaPatchResponse> | TasaPatchResponse>(
        tpvUrl(`merchants/${idMerchant}/tasa/`),
        body,
      )
      .pipe(map((resp) => unwrapTpv(resp) as TasaPatchResponse));
  }

  /**
   * Desenvuelve la respuesta del GET y valida que traiga el bloque `tasas`.
   * Si el backend responde con envelope vacío o forma inesperada, devuelve
   * `null` (la UI pinta los 4 ejes en 0 % como si nada estuviera capturado).
   */
  private toReadOrNull(resp: TpvEnvelope<TasaRead> | TasaRead): TasaRead | null {
    const inner = unwrapTpv(resp) as Partial<TasaRead> | null | undefined;
    if (!inner || typeof inner !== 'object') return null;
    if (!inner.tasas || typeof inner.tasas !== 'object') return null;
    return inner as TasaRead;
  }

  /**
   * 404 (nodo sin tasas configuradas todavía) → `null` para que la UI muestre
   * los 4 ejes vacíos. Cualquier otro error propaga sin tocar.
   */
  private readOrRethrow(err: unknown): Observable<TasaRead | null> {
    const status = (err as { status?: number } | null | undefined)?.status;
    if (status === 404) return of(null);
    throw err;
  }
}
